import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import type { DraftAction } from "../../masterImages/draft";
import { MIN_REGION_SIZE } from "../../masterImages/draft";
import type { Size } from "../../masterImages/geometry";
import type { BrickRegion, Stroke } from "../../masterImages/types";
import type { Outline } from "./useOutlines";
import { ownsKeyboard } from "./keyboard";

export type Tool = "select" | "draw" | "brushAdd" | "brushErase" | "brushSnap";
export type BrushTool = "brushAdd" | "brushErase" | "brushSnap";
export const isBrush = (tool: Tool): tool is BrushTool =>
  tool === "brushAdd" || tool === "brushErase" || tool === "brushSnap";
const STROKE_MODES = { brushAdd: "add", brushErase: "erase", brushSnap: "snap" } as const;
export interface StrokePreview {
  id: string;
  stroke: Stroke;
}
type Edge = "left" | "right" | "top" | "bottom";
interface Point {
  x: number;
  y: number;
}
interface View {
  scale: number;
  panX: number;
  panY: number;
}
type Drag =
  | { kind: "pan"; last: Point }
  | { kind: "move"; gesture: string; origin: Point; applied: Point }
  | { kind: "resize"; gesture: string; edges: Edge[]; origin: Point; applied: Point }
  | { kind: "draw"; origin: Point; current: Point }
  | { kind: "brush"; id: string; points: number[] };

const HANDLE_PX = 6;
export const REGION_COLOURS = { full: "#1677ff", half: "#fa8c16" };
export const HALF_TINT = "rgba(250, 140, 22, 0.18)";
const OUTSIDE_SELECTION = "rgba(0, 0, 0, 0.45)";
const GUIDE_SPACING = 48;
const BRUSH_REACH_PX = 16;

interface Props {
  image: HTMLCanvasElement;
  imageScale: number;
  bounds: Size;
  regions: BrickRegion[];
  selection: string[];
  outlines: Map<string, Outline>;
  showRegions: boolean;
  tool: Tool;
  brushRadius: number;
  guides: boolean;
  dispatch(action: DraftAction): void;
  onStrokePreview(preview: StrokePreview | null): void;
}

const inside = (r: BrickRegion, p: Point) =>
  p.x >= r.x && p.x < r.x + r.width && p.y >= r.y && p.y < r.y + r.height;

const distanceToRegion = (r: BrickRegion, p: Point) =>
  Math.hypot(Math.max(r.x - p.x, 0, p.x - (r.x + r.width)), Math.max(r.y - p.y, 0, p.y - (r.y + r.height)));

// Touch-ups along an edge start just outside the region, so a stroke may start within `reach`
// of it; the topmost region wins when several are equally close.
const nearestRegion = (candidates: BrickRegion[], p: Point, reach: number) => {
  let best: BrickRegion | undefined;
  let bestDistance = Infinity;
  for (const region of candidates) {
    const d = distanceToRegion(region, p);
    if (d <= reach && d <= bestDistance) {
      best = region;
      bestDistance = d;
    }
  }
  return best;
};

const EditorCanvas: React.FC<Props> = ({
  image, imageScale, bounds, regions, selection, outlines, showRegions, tool, brushRadius, guides, dispatch,
  onStrokePreview,
}) => {
  const container = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const cursorCanvas = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const [view, setView] = useState<View | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [spaceDown, setSpaceDown] = useState(false);

  useEffect(() => {
    const observer = new ResizeObserver(([entry]) =>
      setSize({ width: entry.contentRect.width, height: entry.contentRect.height })
    );
    observer.observe(container.current!);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (view || size.width === 0) return;
    const scale = 0.95 * Math.min(size.width / bounds.width, size.height / bounds.height);
    setView({ scale, panX: (size.width - bounds.width * scale) / 2, panY: (size.height - bounds.height * scale) / 2 });
  }, [size, bounds, view]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      if (ownsKeyboard(e.target)) return;
      // Space would otherwise also activate whichever toolbar button still has focus.
      e.preventDefault();
      setSpaceDown(true);
    };
    const up = (e: KeyboardEvent) => e.code === "Space" && setSpaceDown(false);
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  // React registers wheel listeners as passive, so zooming would also scroll the page.
  useEffect(() => {
    const el = canvas.current!;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const r = el.getBoundingClientRect();
      const sx = e.clientX - r.left;
      const sy = e.clientY - r.top;
      setView((v) => {
        if (!v) return v;
        const scale = Math.min(20, Math.max(0.02, v.scale * Math.exp(-e.deltaY * 0.0015)));
        return { scale, panX: sx - ((sx - v.panX) * scale) / v.scale, panY: sy - ((sy - v.panY) * scale) / v.scale };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  useEffect(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx || !view) return;
    const dpr = window.devicePixelRatio || 1;
    el.width = size.width * dpr;
    el.height = size.height * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#1f1f1f";
    ctx.fillRect(0, 0, size.width, size.height);

    const k = view.scale / imageScale;
    ctx.setTransform(dpr * k, 0, 0, dpr * k, dpr * view.panX, dpr * view.panY);
    ctx.drawImage(image, 0, 0);
    const drawGuides = () => {
      if (!guides) return;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      for (let y = GUIDE_SPACING / 2; y < size.height; y += GUIDE_SPACING) {
        ctx.fillStyle = "rgba(0, 0, 0, 0.5)";
        ctx.fillRect(0, Math.round(y) + 1, size.width, 1);
        ctx.fillStyle = "rgba(0, 229, 255, 0.85)";
        ctx.fillRect(0, Math.round(y), size.width, 1);
      }
    };
    if (!showRegions) {
      drawGuides();
      return;
    }
    for (const region of regions) {
      const outline = outlines.get(region.id);
      if (outline) ctx.drawImage(outline.canvas, outline.x, outline.y);
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const toScreen = (x: number, y: number) => [x * view.scale + view.panX, y * view.scale + view.panY];
    if (selection.length > 0) {
      ctx.beginPath();
      ctx.rect(0, 0, size.width, size.height);
      for (const region of regions.filter((r) => selection.includes(r.id))) {
        const [sx, sy] = toScreen(region.x, region.y);
        ctx.rect(sx, sy, region.width * view.scale, region.height * view.scale);
      }
      ctx.fillStyle = OUTSIDE_SELECTION;
      ctx.fill("evenodd");
    }
    for (const region of regions) {
      const [sx, sy] = toScreen(region.x, region.y);
      const w = region.width * view.scale;
      const h = region.height * view.scale;
      const selected = selection.includes(region.id);
      const half = region.type === "half";
      if (half) {
        ctx.fillStyle = HALF_TINT;
        ctx.fillRect(sx, sy, w, h);
        ctx.setLineDash([6, 4]);
      }
      ctx.strokeStyle = REGION_COLOURS[region.type];
      ctx.lineWidth = selected ? 3 : 1.5;
      ctx.strokeRect(sx, sy, w, h);
      ctx.setLineDash([]);
      if (half && w > 24 && h > 18) {
        const cx = sx + w / 2;
        const cy = sy + h / 2;
        ctx.fillStyle = REGION_COLOURS.half;
        ctx.fillRect(cx - 10, cy - 9, 20, 18);
        ctx.fillStyle = "#fff";
        ctx.font = "bold 13px sans-serif";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("½", cx, cy + 0.5);
      }
      if (selected) {
        ctx.fillStyle = "#fff";
        for (const [hx, hy] of [[0, 0], [0.5, 0], [1, 0], [0, 0.5], [1, 0.5], [0, 1], [0.5, 1], [1, 1]]) {
          ctx.fillRect(sx + hx * w - 3, sy + hy * h - 3, 6, 6);
        }
      }
    }
    if (drag?.kind === "draw") {
      const [x0, y0] = toScreen(drag.origin.x, drag.origin.y);
      const [x1, y1] = toScreen(drag.current.x, drag.current.y);
      ctx.setLineDash([6, 4]);
      ctx.strokeStyle = "#fff";
      ctx.lineWidth = 1.5;
      ctx.strokeRect(x0, y0, x1 - x0, y1 - y0);
      ctx.setLineDash([]);
    }
    drawGuides();
  }, [image, imageScale, regions, selection, outlines, showRegions, view, size, drag, tool, brushRadius, guides]);

  const screenPoint = (e: React.PointerEvent): Point => {
    const r = canvas.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };
  const imagePoint = (e: React.PointerEvent): Point => {
    const s = screenPoint(e);
    return { x: (s.x - view!.panX) / view!.scale, y: (s.y - view!.panY) / view!.scale };
  };
  const regionAt = (p: Point) => [...regions].reverse().find((r) => inside(r, p));
  const edgesAt = (p: Point): Edge[] => {
    const tolerance = HANDLE_PX / view!.scale;
    for (const r of regions.filter((r) => selection.includes(r.id))) {
      if (p.x < r.x - tolerance || p.x > r.x + r.width + tolerance) continue;
      if (p.y < r.y - tolerance || p.y > r.y + r.height + tolerance) continue;
      const edges: Edge[] = [];
      if (Math.abs(p.x - r.x) <= tolerance) edges.push("left");
      else if (Math.abs(p.x - (r.x + r.width)) <= tolerance) edges.push("right");
      if (Math.abs(p.y - r.y) <= tolerance) edges.push("top");
      else if (Math.abs(p.y - (r.y + r.height)) <= tolerance) edges.push("bottom");
      if (edges.length) return edges;
    }
    return [];
  };

  const strokeOf = (points: number[]): Stroke => ({
    mode: isBrush(tool) ? STROKE_MODES[tool] : "erase",
    radius: Math.round((brushRadius / view!.scale) * 10) / 10,
    points: points.map((v) => Math.round(v * 10) / 10),
  });

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!view) return;
    e.currentTarget.focus();
    e.currentTarget.setPointerCapture(e.pointerId);
    const p = imagePoint(e);
    if (e.button === 1 || spaceDown || !showRegions) {
      setDrag({ kind: "pan", last: screenPoint(e) });
      return;
    }
    if (tool === "draw") {
      setDrag({ kind: "draw", origin: p, current: p });
      return;
    }
    if (isBrush(tool)) {
      const reach = (brushRadius + BRUSH_REACH_PX) / view.scale;
      const target = nearestRegion(
        selection.length > 0 ? regions.filter((r) => selection.includes(r.id)) : regions,
        p,
        reach
      );
      if (target) {
        const brush: Drag = { kind: "brush", id: target.id, points: [p.x, p.y] };
        setDrag(brush);
        onStrokePreview({ id: target.id, stroke: strokeOf(brush.points) });
      } else if (selection.length > 0) {
        message.info("The brush only paints into the selected regions. Press Esc to paint into any region.");
      } else {
        message.info("Start the brush stroke inside a brick region; it only changes that region's outline.");
      }
      return;
    }
    const edges = edgesAt(p);
    if (edges.length) {
      setDrag({ kind: "resize", gesture: crypto.randomUUID(), edges, origin: p, applied: { x: 0, y: 0 } });
      return;
    }
    const hit = regionAt(p);
    if (!hit) {
      if (!e.shiftKey) dispatch({ type: "select", ids: [] });
      setDrag({ kind: "pan", last: screenPoint(e) });
      return;
    }
    if (e.shiftKey) {
      dispatch({ type: "select", ids: [hit.id], additive: true });
      return;
    }
    if (!selection.includes(hit.id)) dispatch({ type: "select", ids: [hit.id] });
    setDrag({ kind: "move", gesture: crypto.randomUUID(), origin: p, applied: { x: 0, y: 0 } });
  };

  const drawBrushCursor = (at: Point | null) => {
    const el = cursorCanvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx) return;
    const dpr = window.devicePixelRatio || 1;
    if (el.width !== size.width * dpr || el.height !== size.height * dpr) {
      el.width = size.width * dpr;
      el.height = size.height * dpr;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);
    if (!at || !isBrush(tool)) return;
    ctx.beginPath();
    ctx.arc(at.x, at.y, brushRadius, 0, Math.PI * 2);
    ctx.lineWidth = 1;
    ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(at.x, at.y, brushRadius + 1, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(0, 0, 0, 0.6)";
    ctx.stroke();
  };

  useEffect(() => {
    if (!isBrush(tool)) drawBrushCursor(null);
  }, [tool]);

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    drawBrushCursor(screenPoint(e));
    if (!drag || !view) return;
    const p = imagePoint(e);
    if (drag.kind === "pan") {
      const s = screenPoint(e);
      setView({ ...view, panX: view.panX + s.x - drag.last.x, panY: view.panY + s.y - drag.last.y });
      setDrag({ ...drag, last: s });
    } else if (drag.kind === "move" || drag.kind === "resize") {
      const dx = Math.round(p.x - drag.origin.x) - drag.applied.x;
      const dy = Math.round(p.y - drag.origin.y) - drag.applied.y;
      if (dx === 0 && dy === 0) return;
      if (drag.kind === "move") {
        dispatch({ type: "move", dx, dy, gesture: drag.gesture });
      } else {
        dispatch({
          type: "resize",
          gesture: drag.gesture,
          ...(drag.edges.includes("left") && { left: dx }),
          ...(drag.edges.includes("right") && { right: dx }),
          ...(drag.edges.includes("top") && { top: dy }),
          ...(drag.edges.includes("bottom") && { bottom: dy }),
        });
      }
      setDrag({ ...drag, applied: { x: drag.applied.x + dx, y: drag.applied.y + dy } });
    } else if (drag.kind === "draw") {
      setDrag({ ...drag, current: p });
    } else if (drag.kind === "brush") {
      const n = drag.points.length;
      const radius = brushRadius / view.scale;
      if (Math.hypot(p.x - drag.points[n - 2], p.y - drag.points[n - 1]) >= radius / 3) {
        const points = [...drag.points, p.x, p.y];
        setDrag({ ...drag, points });
        onStrokePreview({ id: drag.id, stroke: strokeOf(points) });
      }
    }
  };

  const onPointerUp = () => {
    if (drag?.kind === "draw") {
      const x = Math.min(drag.origin.x, drag.current.x);
      const y = Math.min(drag.origin.y, drag.current.y);
      const width = Math.abs(drag.current.x - drag.origin.x);
      const height = Math.abs(drag.current.y - drag.origin.y);
      if (width >= MIN_REGION_SIZE && height >= MIN_REGION_SIZE) {
        dispatch({
          type: "add",
          region: { id: crypto.randomUUID(), x, y, width, height, type: "full", tolerance: null, strokes: [], edited: true },
        });
      }
    }
    if (drag?.kind === "brush" && view) {
      dispatch({ type: "addStroke", id: drag.id, stroke: strokeOf(drag.points) });
      onStrokePreview(null);
    }
    setDrag(null);
  };

  const cursor =
    drag?.kind === "pan" || spaceDown ? "grabbing" : tool === "draw" ? "crosshair" : tool === "select" ? "default" : "none";

  return (
    <div ref={container} style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <canvas
        ref={canvas}
        tabIndex={0}
        style={{ width: "100%", height: "100%", display: "block", cursor, touchAction: "none", outline: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={() => drawBrushCursor(null)}
      />
      <canvas
        ref={cursorCanvas}
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
      />
    </div>
  );
};

export default EditorCanvas;
